import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  escapeTextToEquivalentRegexp,
  SearchQueryTermType,
  SearchQueryTermWithoutPositions,
  searchRegexLanguageParser,
} from '@neptune/compound-search-domain';
import { getCurrentProjectIdentifier } from '@neptune/current-project-business-logic';

import { addNotification } from 'state/ui/global/notification-popup/actions';

import { useResolveSelectedRunIds } from './use-resolve-selected-run-ids';

type ResolveSelectedRunIds = (
  projectIdentifier: string,
  selectedRunIds?: string[],
) => Promise<{ experimentNames: string[] }>;

type UseCopyCompareSelectionAsSearchQueryParams = {
  runIds: string[];
  resolveSelectedRunIds?: ResolveSelectedRunIds;
};

type CopyCompareSelectionAsSearchQueryParams = {
  projectIdentifier?: string;
  runIds: string[];
  resolveSelectedRunIds: ResolveSelectedRunIds;
  notify: typeof addNotification;
};

export function buildSearchQueryFromNames(names: string[]): string {
  const queryTerms = names.reduce<SearchQueryTermWithoutPositions[]>((terms, name, index) => {
    const escapedName = escapeTextToEquivalentRegexp(name);

    terms.push({
      type: SearchQueryTermType.CRITERION,
      value: `^${escapedName}$`,
    });

    if (index < names.length - 1) {
      terms.push({
        type: SearchQueryTermType.OPERATOR,
        value: 'OR',
      });
    }

    return terms;
  }, []);

  return searchRegexLanguageParser.serialize(queryTerms);
}

async function copyCompareSelectionAsSearchQuery({
  projectIdentifier,
  runIds,
  resolveSelectedRunIds,
  notify,
}: CopyCompareSelectionAsSearchQueryParams): Promise<void> {
  if (!projectIdentifier) {
    return;
  }

  const namesToCopy = new Set<string>();

  if (runIds.length) {
    const { experimentNames } = await resolveSelectedRunIds(projectIdentifier, runIds);

    for (const experimentName of experimentNames) {
      namesToCopy.add(experimentName);
    }
  }

  if (!namesToCopy.size) {
    notify('info', 'No run names found in selection.');
    return;
  }

  const query = buildSearchQueryFromNames(Array.from(namesToCopy));

  try {
    await navigator.clipboard.writeText(query);
    notify('success', 'Copied to clipboard.');
  } catch {
    notify('error', 'Failed to copy to clipboard.');
  }
}

export function useCopyCompareSelectionAsSearchQuery({
  runIds,
}: UseCopyCompareSelectionAsSearchQueryParams) {
  const dispatch = useDispatch();
  const projectIdentifier = useSelector(getCurrentProjectIdentifier);

  const [copyAsSearchQueryLoading, setCopyAsSearchQueryLoading] = React.useState(false);

  const resolveSelectedRunIds = useResolveSelectedRunIds();

  const runIdsRef = React.useRef(runIds);

  React.useEffect(() => {
    runIdsRef.current = runIds;
  }, [runIds]);

  const copyAsSearchQuery = React.useCallback(async () => {
    try {
      setCopyAsSearchQueryLoading(true);

      await copyCompareSelectionAsSearchQuery({
        projectIdentifier,
        runIds: runIdsRef.current,
        resolveSelectedRunIds,
        notify: (type, message) => dispatch(addNotification(type, message)),
      });
    } catch {
      dispatch(addNotification('error', 'Failed to copy to clipboard.'));
    } finally {
      setCopyAsSearchQueryLoading(false);
    }
  }, [projectIdentifier, resolveSelectedRunIds, dispatch]);

  return {
    copyAsSearchQuery,
    copyAsSearchQueryLoading,
  };
}
